import { GetPaginatedTendersQueryVariables, TenderSortField, SortOrder, TenderFilters } from "@/graphql/__generated__/graphql";
import { format } from "date-fns";
import { usePaginationState, updatePaginationState } from "@/store/paginationStore";
import { ActiveFilters, TenderFiltersDialog, TendersSortingDropdown } from "./Components";
import { Toggle } from "@/components/ui/toggle";
import { isEditor } from "@/utils/authentication";
import useUserStore from "@/store/shield";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { RefreshCcwIcon } from "lucide-react";

interface Props {
  onFilterSubmit: (_variables: GetPaginatedTendersQueryVariables) => void;
  displayAmount: number;
}

/**
 * TendersFilter component handles filtering and sorting of tenders
 * It manages filter state and provides UI for filter controls
 * @param props Component properties
 * @returns JSX element
 */
const TendersFilter = (props: Props) => {

  // Extract props
  const { onFilterSubmit, displayAmount } = props;

  // Hooks
  const { t } = useTranslation(["components"]);

  // Stores
  const { userId } = useUserStore();

  // Get pagination state from global store
  const paginationState: PaginationState["paginatedTenders"] = usePaginationState("paginatedTenders");
  // Destructure filter values and sort settings from pagination state
  const { filters: {
    archived,
    startDate,
    deadlineDate,
    state,
    search,
    operationalOrgs,
    chanceAdmins,
    revenue,
    surcharge,
  }, sort } = paginationState as any;

  // Constants - Only check if userId is valid to prevent undefined/null comparisons
  const isShowingYourChancesOnly = userId != null && (paginationState.filters?.chanceAdmins?.length === 1 &&
    paginationState.filters?.chanceAdmins?.[0] === userId);

  /**
   * Handles filter submission with formatted date values
   * Can accept new filter values or use current state
   * @param newValues Optional new filter values to submit
   */
  const handleSubmitClick = (newValues?: GetPaginatedTendersQueryVariables) => {
    // Format start date range to ISO strings with time boundaries
    const startDateGte = startDate?.gte ? format(new Date(startDate.gte), "yyyy-MM-dd") + "T00:00:00+00:00" : null;
    const startDateLte = startDate?.lte ? format(new Date(startDate.lte), "yyyy-MM-dd") + "T23:59:59+00:00" : null;

    // Format deadline date range to ISO strings with time boundaries
    const deadlineDateGte = deadlineDate?.gte ? format(new Date(deadlineDate.gte), "yyyy-MM-dd") + "T00:00:00+00:00" : null;
    const deadlineDateLte = deadlineDate?.lte ? format(new Date(deadlineDate.lte), "yyyy-MM-dd") + "T23:59:59+00:00" : null;

    if (newValues) {
      /**
       * If new values are provided, use them but ensure sort is preserved if needed
       * If we intentionally include a `sort` key with a null value (to indicate a reset),
       * our code will honor that and not override it with the outdated sort value from the store.
       */
      if (!Object.prototype.hasOwnProperty.call(newValues, "sort") && sort) {
        // Add current sort settings if not explicitly provided in newValues
        newValues.sort = {
          field: sort.field as TenderSortField,
          order: sort.order as SortOrder
        };
      }

      // Submit the new values to parent component
      onFilterSubmit(newValues);
    } else {
      // If no new values, construct query variables from current state
      const submitVariables: GetPaginatedTendersQueryVariables = {
        first: displayAmount,
        filters: {
          archived,
          startDate: { gte: startDateGte, lte: startDateLte },
          deadlineDate: { gte: deadlineDateGte, lte: deadlineDateLte },
          state,
          search,
          operationalOrgs,
          chanceAdmins,
          revenue,
          surcharge,
        }
      };

      // Only include sort parameters if they exist in state
      if (sort) {
        submitVariables.sort = {
          field: sort.field as TenderSortField,
          order: sort.order as SortOrder
        };
      }

      // Submit constructed variables to parent component
      onFilterSubmit(submitVariables);
    };
  };

  /**
   * Resets all filter values while optionally preserving sort settings
   * Updates both local state and triggers query update
   */
  const handleResetClickFilters = (preserveSort?: boolean) => {
    // Define empty filters object
    const emptyFilters = {
      archived: null,
      state: null,
      search: null,
      startDate: null,
      deadlineDate: null,
      operationalOrgs: null,
      chanceAdmins: null,
      revenue: null,
      surcharge: null,
    };

    // Reset all filter values in the global store
    updatePaginationState({
      paginatedTenders: {
        filters: emptyFilters as TenderFilters,
        sort: preserveSort ? sort : null
      }
    });

    // Create reset query variables with explicitly empty filters
    const updatedVariables: GetPaginatedTendersQueryVariables = {
      first: displayAmount,
      filters: {
        archived: null,
        state: null,
        search: null,
        startDate: null,
        deadlineDate: null,
        operationalOrgs: null,
        chanceAdmins: null,
        revenue: null,
        surcharge: null,
      }
    };

    if (preserveSort && sort) {
      updatedVariables.sort = {
        field: sort.field as TenderSortField,
        order: sort.order as SortOrder
      };
    }

    // Immediately submit the reset filter state to parent component
    onFilterSubmit(updatedVariables);
  };

  const handleYourChancesChange = () => {
    // Early return if userId is not valid to prevent undefined/null in GraphQL queries
    if (userId == null) {
      console.warn("Cannot filter by your chances: userId is not available");
      return;
    }

    // Check if chanceAdmins contains exactly one element and that element is the current userId
    const isCurrentlyFiltered = paginationState.filters?.chanceAdmins?.length === 1 &&
      paginationState.filters?.chanceAdmins?.[0] === userId;
    const newChanceAdmins = isCurrentlyFiltered ? null : [userId];

    // Update the store
    updatePaginationState({
      paginatedTenders: {
        filters: {
          ...paginationState.filters,
          chanceAdmins: newChanceAdmins
        }
      }
    });

    // Create updated filter variables with the new chanceAdmins value
    const updatedVariables: GetPaginatedTendersQueryVariables = {
      first: displayAmount,
      filters: {
        ...paginationState.filters,
        chanceAdmins: newChanceAdmins
      }
    };

    // Include sort if it exists
    if (sort) {
      updatedVariables.sort = {
        field: sort.field as TenderSortField,
        order: sort.order as SortOrder
      };
    }

    // Submit the updated variables
    handleSubmitClick(updatedVariables);
  };

  return (
    <div className="mb-1 -mt-4 pt-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">

          {/* Filters Dialog - Opens modal with all filter options */}
          <TenderFiltersDialog
            displayAmount={displayAmount}
            handleResetClickFilters={handleResetClickFilters}
            handleSubmitClick={handleSubmitClick}
          />

          {/* Sorting Dropdown - Controls sort field and direction */}
          <TendersSortingDropdown
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
          />

          {/* Your Chances Toggle - Controls whether to show only the chances of the current user (Editors only) */}
          {isEditor() && userId != null && (
            <Toggle variant="outline"
              pressed={isShowingYourChancesOnly}
              onPressedChange={handleYourChancesChange}
              className="data-[state=on]:bg-slate-600 data-[state=on]:text-primary-foreground"
            >
              {t("filters.tenders.yourchances")}
            </Toggle>
          )}

          {/* Active Filters - Shows and allows removal of applied filters */}
          <ActiveFilters
            displayAmount={displayAmount}
            handleSubmitClick={handleSubmitClick}
            handleResetClickFilters={handleResetClickFilters}
          />

        </div>
        <div>
          <Button variant="outline" size="icon" onClick={() => handleResetClickFilters(false)}>
            <RefreshCcwIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TendersFilter;
